import {useEffect, useId, useRef, useState} from 'react';
import {GoogleServices, type GoogleService} from '../src/google-types';
import {ErrorNotice, Loading} from './components';
import type {HermesState} from './lib/types';
import type {hermesActions} from './lib/store';
import {useSearch} from './router';
import './google-settings.css';

export function GoogleSettings({state, actions}: {state: HermesState; actions: typeof hermesActions}) {
  const [draftServices, setDraftServices] = useState<GoogleService[] | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const search = useSearch();
  const accountId = state.session.data?.accountId;
  const status = state.google.data;
  const services = draftServices ?? (status?.connected ? status.services : GoogleServices.map(service => service.id));
  const configured = state.config.status === 'ready' && state.config.data?.googleConfigured === true;
  const busy = state.operation.label !== null;
  useEffect(() => {
    if (!accountId) return;
    const read = () => {if (document.visibilityState === 'visible') void actions.refreshGoogle().catch(() => undefined);};
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, [accountId, actions]);
  useEffect(() => {
    if (new URLSearchParams(search).get('connect') !== 'google') return;
    heading.current?.scrollIntoView({block: 'start'});
    heading.current?.focus({preventScroll: true});
  }, [search]);
  return <section className="settings-section settings-google" aria-labelledby={titleId}>
    <div className="settings-description"><h2 ref={heading} id={titleId} tabIndex={-1}>Google</h2>
      <p>Let your team use your email, files, sheets and calendar. Choose the services to connect.</p>
      {state.config.status === 'ready' && !configured && <p>Google connection setup is in progress.</p>}
      {status?.connected && <><p className="settings-key-status">Connected to {status.email}.</p>
        <p>{GoogleServices.filter(service => status.services.includes(service.id)).map(service => service.name).join(', ')}</p></>}
      {status?.signInLinked && <p>Google sign-in is enabled for this EightBots account.</p>}
    </div>
    <div><ErrorNotice error={state.google.error}/>
      {!status && state.google.status === 'loading' && <Loading/>}
      <fieldset className="google-service-choices" disabled={busy || !configured}><legend>Services</legend>
        {GoogleServices.map(service => <label key={service.id}><input type="checkbox" checked={services.includes(service.id)}
          onChange={event => setDraftServices(event.target.checked ? [...services, service.id] : services.filter(id => id !== service.id))}/>{service.name}</label>)}
      </fieldset>
      <div className="button-row"><button type="button" className="button" disabled={busy || !configured || services.length === 0}
        onClick={() => void actions.connectGoogle(services).catch(() => undefined)}>{status?.connected ? 'Change Google access' : 'Connect Google'}</button>
        {status?.connected && <button type="button" className="text-button" disabled={busy || !configured}
          onClick={() => void actions.disconnectGoogle().catch(() => undefined)}>Disconnect Google</button>}
        {status && !status.signInLinked && <button type="button" className="text-button" disabled={busy || !configured}
          onClick={() => void actions.linkGoogleSignIn().catch(() => undefined)}>Enable Google sign-in</button>}
      </div>
    </div>
  </section>;
}
